import type { TFunction } from "i18next";

import { statusIntent, type IntentName } from "@/design";

import { AUDIT_MODULES, AUDIT_RESULTS } from "./url-state";

const MODULE_SET = new Set<string>(AUDIT_MODULES);
const RESULT_SET = new Set<string>(AUDIT_RESULTS);

/**
 * `module` is a closed BE enum (GlobalAuditModule) — localized via the
 * "audit" i18n namespace (`module.<code>`). Falls back to the raw code for a
 * value outside the known set (shouldn't happen since the schema is a closed
 * enum, but never renders blank).
 */
export function moduleLabel(t: TFunction, module: string): string {
  return MODULE_SET.has(module) ? t(`audit:module.${module}`) : module;
}

/** `result` is a closed BE enum (GlobalAuditResult) — same pattern as module. */
export function resultLabel(t: TFunction, result: string): string {
  return RESULT_SET.has(result) ? t(`audit:result.${result}`) : result;
}

/** Result -> Badge intent, via the canonical status taxonomy (CLAUDE.md rule 11). */
export function resultIntent(result: string): IntentName {
  return statusIntent("audit", result);
}

/**
 * Turns a snake/dot-cased backend code into a readable phrase
 * ("sino.status_update_blocked" -> "Sino status update blocked").
 *
 * `activity` (`GlobalAuditEvent.activity`) is an open backend-defined string —
 * the contract declares a plain `string`, not an enum, because every producer
 * mints its own codes and new ones ship over time. Real copy lives in the
 * i18n catalog (`activity.<code>.title`, resolved by lib/summary.ts); this is
 * the last-resort rendering for a code that shipped before its copy did, and
 * the fallback label for an unknown metadata key.
 */
export function humanize(code: string): string {
  const words = code
    .split(/[._-]+/)
    .map((w) => w.trim())
    .filter(Boolean);
  if (words.length === 0) return code;
  // Sentence case, not title case (UX writing rule 1).
  const sentence = words.join(" ").toLowerCase();
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}
